import { useCallback, useEffect, useState } from 'react';
import styles from './Listings.module.css';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import SearchInput from '../../components/SearchInput/SearchInput';
import Button from '../../components/Button/Button';
import Modal, { ModalTitle, ModalDivider, DetailRow } from '../../components/Modal/Modal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import Pagination from '../../components/Pagination/Pagination';
import FilterSelect from '../../components/FilterSelect/FilterSelect';
import Pill from '../../components/Pill/Pill';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ImageLightbox from '../../components/ImageLightbox/ImageLightbox';
import CoinIcon from '../../icons/CoinIcon';
import EditListingModal from './EditListingModal';
import { api, mapListing, describeChanges, deliveryFeeLabel } from '../../lib/api';
import { usePoll } from '../../lib/usePoll';
import { unitLabel } from '../../lib/units';

// What a restock costs the seller. Hardcoding it here is how this page ends up
// promising one price while the server charges another, so it is only the
// fallback for the moment before GET /points/costs lands.
const FALLBACK_RESTOCK_COST = 10;

const CATEGORY_PARAM = { Produce: 'produce', Livestock: 'livestock', Equipment: 'equipment' };
const STATUS_PARAM = { Active: 'active', Pending: 'pending', Expired: 'expired' };

// "12 Mar 2027", or a dash for a listing that has never been approved and so
// has no clock running yet.
const formatExpiry = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const Listings = () => {
    const [listings, setListings] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const [page, setPage] = useState(1);
    const [viewedItem, setViewedItem] = useState(null);
    const [restockItem, setRestockItem] = useState(null);
    const [restockQty, setRestockQty] = useState('1');
    const [removeItem, setRemoveItem] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);
    const [declineItem, setDeclineItem] = useState(null);
    const [editItem, setEditItem] = useState(null);
    // The photo shown full size, opened by clicking any listing image.
    const [lightbox, setLightbox] = useState(null);
    const [restockCost, setRestockCost] = useState(FALLBACK_RESTOCK_COST);

    // The server owns the points economy — pull the live restock cost so this
    // page can't quote a stale price at a seller it is about to charge.
    useEffect(() => {
        let cancelled = false;
        api('/points/costs', { auth: false })
            .then(({ costs }) => {
                if (!cancelled && costs?.restock != null) setRestockCost(costs.restock);
            })
            .catch(() => {}); // keep the fallback
        return () => {
            cancelled = true;
        };
    }, []);

    const loadListings = useCallback(async () => {
        const params = new URLSearchParams({ page: String(page), limit: '20' });
        if (search.trim()) params.set('search', search.trim());
        if (CATEGORY_PARAM[categoryFilter]) params.set('category', CATEGORY_PARAM[categoryFilter]);
        if (STATUS_PARAM[statusFilter]) params.set('status', STATUS_PARAM[statusFilter]);
        try {
            const data = await api(`/admin/listings?${params.toString()}`);
            setListings(data.listings.map(mapListing));
            setTotalPages(Math.max(1, data.pages));
        } catch {
            setListings([]);
        }
    }, [search, categoryFilter, statusFilter, page]);

    useEffect(() => {
        loadListings();
    }, [loadListings]);

    // Nothing pushes to the portal. A seller waiting on approval is blocked until an admin sees the
    // submission, so the queue has to refresh itself.
    usePoll(loadListings, 30000);

    const filteredListings = listings;

    const openRestockModal = (item) => {
        setViewedItem(null);
        setRestockQty('1');
        setRestockItem(item);
    };

    const openRemoveModal = (item) => {
        setViewedItem(null);
        setRemoveItem(item);
    };

    const openDeclineModal = (item) => {
        setViewedItem(null);
        setDeclineItem(item);
    };

    const openEditModal = (item) => {
        setViewedItem(null);
        setEditItem(item);
    };

    const handleEdited = async () => {
        setEditItem(null);
        await loadListings();
    };

    const openDeleteModal = (item) => {
        setViewedItem(null);
        setDeleteItem(item);
    };

    const handleApprove = async (item) => {
        try {
            await api(`/admin/listings/${item._id}/approve`, { method: 'POST' });
            await loadListings();
        } catch (err) {
            alert(err.message);
        }
        setViewedItem(null);
    };

    const handleApproveChanges = async (item) => {
        try {
            await api(`/admin/listings/${item._id}/changes/approve`, { method: 'POST' });
            await loadListings();
        } catch (err) {
            alert(err.message);
        }
        setViewedItem(null);
    };

    const handleDeclineChanges = async (item) => {
        try {
            await api(`/admin/listings/${item._id}/changes/decline`, { method: 'POST' });
            await loadListings();
        } catch (err) {
            alert(err.message);
        }
        setViewedItem(null);
    };

    const handleRestock = async () => {
        const quantity = Number(restockQty);
        try {
            await api(`/admin/listings/${restockItem._id}/restock`, { method: 'POST', body: { stockKg: quantity } });
            await loadListings();
        } catch (err) {
            alert(err.message);
        }
        setRestockItem(null);
    };

    const handleRemove = async () => {
        try {
            await api(`/admin/listings/${removeItem._id}`, { method: 'DELETE' });
            await loadListings();
        } catch (err) {
            alert(err.message);
        }
        setRemoveItem(null);
    };

    // Unlike Remove, this deletes the row itself — see DELETE
    // /api/admin/listings/:id/permanent for what else it clears up.
    const handleDelete = async () => {
        try {
            await api(`/admin/listings/${deleteItem._id}/permanent`, { method: 'DELETE' });
            await loadListings();
        } catch (err) {
            alert(err.message);
        }
        setDeleteItem(null);
    };

    const handleDecline = async () => {
        try {
            await api(`/admin/listings/${declineItem._id}/decline`, { method: 'POST' });
            await loadListings();
        } catch (err) {
            alert(err.message);
        }
        setDeclineItem(null);
    };

    return (
        <div className={`${styles.card} p-4`}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <h2 className={`${styles.cardTitle} mb-0`}>All Listings</h2>

                <div className="d-flex align-items-center flex-wrap gap-3">
                    <FilterSelect
                        label="Category"
                        value={categoryFilter}
                        options={['All', 'Produce', 'Livestock', 'Equipment']}
                        onChange={setCategoryFilter}
                    />
                    <FilterSelect
                        label="Status"
                        value={statusFilter}
                        options={['All', 'Active', 'Pending', 'Expired']}
                        onChange={setStatusFilter}
                    />
                    <SearchInput
                        placeholder="Search for listings..."
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>
            </div>

            <div className={`${styles.tableWrap} table-responsive mt-3`}>
                <table className={`${styles.table} w-100`}>
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th>Category</th>
                            <th>Seller</th>
                            <th>Price</th>
                            <th>Stock</th>
                            <th>Expires</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredListings.map((item) => (
                            <tr key={item._id}>
                                <td>
                                    <div className="d-flex align-items-center gap-3">
                                        <button
                                            type="button"
                                            className={`${styles.itemThumb} ${styles.zoomable}`}
                                            aria-label={`View photo of ${item.name}`}
                                            onClick={() => setLightbox({ src: item.image, alt: item.name })}
                                        >
                                            <img src={item.image} alt={item.name} />
                                        </button>
                                        <span className={styles.itemName}>{item.name}</span>
                                    </div>
                                </td>
                                <td><Pill>{item.category}</Pill></td>
                                <td className={styles.seller}>{item.seller}</td>
                                <td className={styles.price}>{item.price}</td>
                                <td className={styles.stock}>{item.stock} {item.stockUnit}</td>
                                <td className={styles.stock}>{formatExpiry(item.expiresAt)}</td>
                                <td>
                                    <div className="d-flex align-items-center gap-2">
                                        <StatusBadge status={item.status} />
                                        {item.hasPendingChanges && <StatusBadge status="Changes pending" />}
                                    </div>
                                </td>
                                <td className="text-end position-relative">
                                    <ActionMenu
                                        ariaLabel="Listing actions"
                                        items={
                                            // An expired listing has already been through review, so it
                                            // gets the live listing's actions rather than approve/decline.
                                            // Restocking is what an admin can do to revive one; renewing
                                            // costs the seller points and stays the seller's call.
                                            item.status === 'Active' || item.status === 'Expired'
                                                ? [
                                                    { label: 'View listing', onClick: () => setViewedItem(item) },
                                                    { label: 'Edit listing', onClick: () => openEditModal(item) },
                                                    ...(item.hasPendingChanges
                                                        ? [
                                                            { label: 'Approve changes', onClick: () => handleApproveChanges(item) },
                                                            { label: 'Decline changes', danger: true, onClick: () => handleDeclineChanges(item) },
                                                        ]
                                                        : []),
                                                    { label: 'Restock item', onClick: () => openRestockModal(item) },
                                                    { label: 'Remove item', danger: true, onClick: () => openRemoveModal(item) },
                                                    { label: 'Delete permanently', danger: true, onClick: () => openDeleteModal(item) },
                                                ]
                                                : [
                                                    { label: 'View listing', onClick: () => setViewedItem(item) },
                                                    { label: 'Edit listing', onClick: () => openEditModal(item) },
                                                    { label: 'Approve item', onClick: () => handleApprove(item) },
                                                    { label: 'Decline item', danger: true, onClick: () => openDeclineModal(item) },
                                                    { label: 'Delete permanently', danger: true, onClick: () => openDeleteModal(item) },
                                                ]
                                        }
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} totalPages={totalPages} onChange={setPage} />

            {viewedItem && (
                <Modal onClose={() => setViewedItem(null)}>
                    <div className="d-flex align-items-center gap-3">
                        <button
                            type="button"
                            className={`${styles.modalThumb} ${styles.zoomable}`}
                            aria-label={`View photo of ${viewedItem.name}`}
                            onClick={() => setLightbox({ src: viewedItem.image, alt: viewedItem.name })}
                        >
                            <img src={viewedItem.image} alt={viewedItem.name} />
                        </button>
                        <div>
                            <ModalTitle className="mb-1">{viewedItem.name}</ModalTitle>
                            <div className={styles.seller}>{viewedItem.seller}</div>
                        </div>
                    </div>

                    <div className="mt-3">
                        <StatusBadge status={viewedItem.status} />
                    </div>

                    <ModalDivider />

                    <DetailRow label="Category" className="mb-3"><Pill>{viewedItem.category}</Pill></DetailRow>
                    <DetailRow label="Pricing model" className="mb-3">
                        <span className="d-inline-flex gap-2">
                            {viewedItem.pricing.map((model) => (
                                <Pill key={model}>{model}</Pill>
                            ))}
                        </span>
                    </DetailRow>
                    <DetailRow label="Price" className="mb-3"><span className={styles.price}>{viewedItem.price}</span></DetailRow>
                    <DetailRow label="In stock"><span className={styles.stock}>{viewedItem.stock} {viewedItem.stockUnit}</span></DetailRow>

                    <ModalDivider />

                    {(viewedItem.status === 'Active' || viewedItem.status === 'Expired') && (
                        <>
                            <DetailRow label="Total sales" className="mb-3"><span>{viewedItem.totalSales}</span></DetailRow>
                            <DetailRow
                                label={viewedItem.status === 'Expired' ? 'Expired' : 'Expires'}
                                className="mb-3"
                            >
                                <span className={styles.stock}>{formatExpiry(viewedItem.expiresAt)}</span>
                            </DetailRow>
                        </>
                    )}
                    <DetailRow label="Rating" className="mb-3"><span className={styles.rating}>★ {viewedItem.rating}</span></DetailRow>
                    <DetailRow label="Seller points" className="mb-3">
                        <span className={`${styles.points} d-inline-flex align-items-center gap-2`}>
                            <CoinIcon size={16} />
                            {viewedItem.sellerPoints} points
                        </span>
                    </DetailRow>

                    {/* Buyers are told how they get the goods, so the review
                        that lets a listing go live has to show the same thing —
                        pickup instructions in particular are free text the
                        seller writes and every buyer reads. */}
                    <DetailRow label="Handover" className="mb-3">
                        <span className="d-inline-flex gap-2">
                            {viewedItem.pickup && <Pill>Pickup</Pill>}
                            {viewedItem.delivery && (
                                <Pill>Delivery &middot; {deliveryFeeLabel(viewedItem.deliveryFee)}</Pill>
                            )}
                            {!viewedItem.pickup && !viewedItem.delivery && <Pill>None</Pill>}
                        </span>
                    </DetailRow>
                    {viewedItem.pickup && viewedItem.pickupInstructions && (
                        <DetailRow label="Pickup instructions">
                            <span>{viewedItem.pickupInstructions}</span>
                        </DetailRow>
                    )}

                    {viewedItem.hasPendingChanges && (
                        <>
                            <ModalDivider />
                            <div className={styles.pendingHeading}>Requested changes (awaiting approval)</div>
                            {describeChanges(viewedItem.pendingChanges, viewedItem.unit).map((change) => (
                                <DetailRow key={change.label} label={change.label} className="mb-2">
                                    {change.key === 'image' ? (
                                        <span className="d-inline-flex align-items-center gap-3">
                                            <button
                                                type="button"
                                                className={`${styles.changeImageOld} ${styles.zoomable}`}
                                                onClick={() => setLightbox({ src: viewedItem.image, alt: 'Current photo' })}
                                            >
                                                <img src={viewedItem.image} alt="Current" />
                                            </button>
                                            <span aria-hidden>→</span>
                                            <button
                                                type="button"
                                                className={`${styles.changeImageNew} ${styles.zoomable}`}
                                                onClick={() => setLightbox({ src: change.value, alt: 'New photo' })}
                                            >
                                                <img src={change.value} alt="New" />
                                            </button>
                                        </span>
                                    ) : (
                                        <span>{change.value}</span>
                                    )}
                                </DetailRow>
                            ))}
                            <div className="d-flex gap-3 mt-3">
                                <Button variant="outline-danger" className="flex-fill" onClick={() => handleDeclineChanges(viewedItem)}>
                                    Decline changes
                                </Button>
                                <Button className="flex-fill" onClick={() => handleApproveChanges(viewedItem)}>
                                    Approve changes
                                </Button>
                            </div>
                        </>
                    )}

                    <ModalDivider />

                    <Button variant="outline" className="w-100 mt-4" onClick={() => openEditModal(viewedItem)}>
                        Edit listing
                    </Button>

                    <div className="d-flex gap-3 mt-3">
                        {viewedItem.status === 'Active' || viewedItem.status === 'Expired' ? (
                            <>
                                <Button variant="outline-danger" className="flex-fill" onClick={() => openRemoveModal(viewedItem)}>
                                    Remove item
                                </Button>
                                <Button className="flex-fill" onClick={() => openRestockModal(viewedItem)}>
                                    Restock item
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="outline-danger" className="flex-fill" onClick={() => openDeclineModal(viewedItem)}>
                                    Decline item
                                </Button>
                                <Button className="flex-fill" onClick={() => handleApprove(viewedItem)}>
                                    Approve item
                                </Button>
                            </>
                        )}
                    </div>
                </Modal>
            )}

            {restockItem && (
                <Modal onClose={() => setRestockItem(null)}>
                    <ModalTitle>Restock item</ModalTitle>

                    <div className={`${styles.restockSummary} d-flex align-items-center gap-3 p-3 mt-4`}>
                        <div className={styles.modalThumb}>
                            <img src={restockItem.image} alt={restockItem.name} />
                        </div>
                        <div>
                            <div className={styles.restockName}>{restockItem.name}</div>
                            <div className={styles.restockPrice}>
                                Listed at <strong>{restockItem.price}</strong>
                            </div>
                        </div>
                    </div>

                    <label htmlFor="restock-qty" className={`${styles.inputLabel} mt-4 mb-2`}>
                        Quantity ({unitLabel(restockItem.unit)}) to add*
                    </label>
                    <input
                        id="restock-qty"
                        type="number"
                        min="1"
                        className={`${styles.qtyInput} w-100`}
                        value={restockQty}
                        onChange={(event) => setRestockQty(event.target.value)}
                    />

                    <div className={`${styles.restockNote} p-3 mt-3`}>
                        Restocking costs {restockCost} points and you have {restockItem.sellerPoints} remaining points
                    </div>

                    <Button
                        className="w-100 mt-4"
                        disabled={!restockQty || Number(restockQty) <= 0 || restockItem.sellerPoints < restockCost}
                        onClick={handleRestock}
                    >
                        Restock (-{restockCost} pts)
                    </Button>
                </Modal>
            )}

            {editItem && (
                <EditListingModal item={editItem} onClose={() => setEditItem(null)} onSaved={handleEdited} />
            )}

            {removeItem && (
                <ConfirmModal
                    title="Remove item"
                    confirmLabel="Remove item"
                    onConfirm={handleRemove}
                    onClose={() => setRemoveItem(null)}
                />
            )}

            {deleteItem && (
                <ConfirmModal
                    title="Delete permanently"
                    message={`This deletes "${deleteItem.name}" for good, along with its photo. It is removed from buyers' carts and any open offers on it are closed. Past orders keep their details. This cannot be undone.`}
                    confirmLabel="Delete permanently"
                    onConfirm={handleDelete}
                    onClose={() => setDeleteItem(null)}
                />
            )}

            {declineItem && (
                <ConfirmModal
                    title="Decline item listing"
                    confirmLabel="Decline item"
                    onConfirm={handleDecline}
                    onClose={() => setDeclineItem(null)}
                />
            )}

            {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
        </div>
    );
};

export default Listings;
